import { useAppState } from "context/AppContext ";
import { motion } from "framer-motion";
import i18next from "i18next";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import appleStoreIcon from "../../assets/images/appleStoreImage.png";
import productLogo from "../../assets/images/appLogoWhite.png";
import googlePlayIcon from "../../assets/images/googlePlayImage.png";
import gridImage from "../../assets/images/gridImage.png";

// React Icons
import { FaEnvelope, FaLinkedinIn, FaTwitter, FaYoutube } from "react-icons/fa";
import {
    TbArrowRight,
    TbCalendarEvent,
    TbChevronRight,
    TbLayersIntersect,
    TbRocket,
    TbUsersGroup,
    TbWorld
} from "react-icons/tb";

/* -------------------------------------------------------------------------- */
/* Data Constants                                                             */
/* -------------------------------------------------------------------------- */

const PLATFORM_LINKS = ["Overview", "Features", "Services"];
const SOLUTIONS_LINKS = ["Health", "Education", "Government", "NGOs"];
const GET_STARTED_LINKS = ["RegisterNow", "Login", "RequestaDemo"];

const LANGUAGES = [
    { code: "en", label: "English" },
    { code: "fr", label: "French" },
    { code: "hi", label: "Hindi" },
];

/* -------------------------------------------------------------------------- */
/* Main Footer Component                                                      */
/* -------------------------------------------------------------------------- */

const Footer = () => {
    const { 
        setActiveLink, 
        setIsDropdownOpen, 
        setDropdownContent, 
        toggledropdown,
        lang, 
        setLang 
    } = useAppState(); 
    
    const { t } = useTranslation();

    // Language Dropdown States & Refs
    const dropdownRef = useRef(null);
    const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);

    // Handlers
    const handleLinkClick = (path) => {
        setActiveLink(path);
        setIsDropdownOpen(false);
        setDropdownContent(null);
    };

    const toggleLanguageDropdown = () => {
        setLanguageDropdownOpen(!languageDropdownOpen);
    };

    const handleLanguageChange = (languageCode) => {
        setLang(languageCode);
        i18next.changeLanguage(languageCode);
        localStorage.setItem("selectedLanguage", languageCode);
        setLanguageDropdownOpen(false);
    };

    // Click outside listener
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setLanguageDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Set initial language
    useEffect(() => {
        const savedLang = localStorage.getItem("selectedLanguage") || "en";
        setLang(savedLang);
        i18next.changeLanguage(savedLang);
    }, [setLang]);

    const activeLanguageLabel = LANGUAGES.find(l => l.code === (lang || "en"))?.label || "English";

    // Animation Variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2, delayChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    return (
        <footer className="w-full px-5  bg-[#0a1a31] text-slate-300 font-inter relative overflow-hidden border-t border-[#162130]">
            
            {/* --- BACKGROUND EFFECTS & PATTERNS --- */}
            <div className="absolute top-0 right-0 w-[50%] h-[100%] bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#112538] via-transparent to-transparent opacity-40 pointer-events-none" />
            
            <div className="absolute top-[10%] w-[100px] md:w-[200px] left-[-2%] rotate-45 opacity-60 pointer-events-none">
                <img src={gridImage} alt="grid_image" />
            </div>
            <div className="absolute bottom-[20%] w-[100px] md:w-[200px] right-[2%] opacity-60 pointer-events-none">
                <img src={gridImage} alt="grid_image" />
            </div>

            <motion.div 
                className="max-w-[1440px] mx-auto pt-16 pb-8 relative z-10"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
            >
                {/* --- TOP GRID SECTION --- */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-0 mb-4">
                    
                    {/* Column 1: Left Info & Features */}
                    <motion.div variants={itemVariants} className="lg:col-span-3 lg:pr-10 lg:border-r border-[#1C2838]">
                        <Link to="/" onClick={() => handleLinkClick("/")}>
                            <img src={productLogo} alt="i2Pulse Logo" className="h-8 md:h-10 mb-6 object-contain" />
                        </Link>
                        <p className="text-sm text-white leading-relaxed mb-8 pr-4">
                            {t("footer.description")}
                        </p>

                        <div className="flex items-center gap-2 mt-1 relative z-20">
                            <a href="https://play.google.com/store/apps/details?id=com.i2pulse&hl=en_IN" target="_blank" rel="noopener noreferrer">
                                <img src={googlePlayIcon} alt="google_play" className="w-[100px] sm:w-[120px] object-contain" />
                            </a>
                            <a href="https://apps.apple.com/in/app/i2pulse/id6748899820?platform=ipad" target="_blank" rel="noopener noreferrer">
                                <img src={appleStoreIcon} alt="apple_store" className="w-[100px] sm:w-[120px] object-contain" />
                            </a>
                        </div>
                    </motion.div>

                    {/* Column 2: Center Menus */}
                    <motion.div variants={itemVariants} className="lg:col-span-6 lg:px-12 lg:border-r border-[#1C2838]">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-8 relative z-20">
                            
                            {/* Platform */}
                            <div>
                                <h3 className="flex items-center gap-2 text-white text-sm font-semibold tracking-wide mb-6">
                                    <TbLayersIntersect className="text-[#13B5A6] w-5 h-5" /> {t("footer.platform")}
                                </h3>
                                <ul className="flex flex-col gap-4">
                                    {PLATFORM_LINKS.map(link => (
                                        <li key={link}>
                                            <Link to="/" onClick={() => handleLinkClick("/")} className="group flex items-center justify-between text-[13px] text-white hover:text-white transition-colors w-full sm:w-[85%]">
                                                {t(`footer.links.${link}`)} <TbChevronRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#13B5A6]" />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Solutions */}
                            <div>
                                <h3 className="flex items-center gap-2 text-white text-sm font-semibold tracking-wide mb-6">
                                    <TbUsersGroup className="text-[#13B5A6] w-5 h-5" /> {t("footer.solutions")}
                                </h3>
                                <ul className="flex flex-col gap-4">
                                    {SOLUTIONS_LINKS.map(link => (
                                        <li key={link}>
                                            <Link to="/" onClick={() => handleLinkClick("/")} className="group flex items-center justify-between text-[13px] text-white hover:text-white transition-colors w-full sm:w-[85%]">
                                                {t(`footer.links.${link}`)} <TbChevronRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#13B5A6]" />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Get Started */}
                            <div>
                                <h3 className="flex items-center gap-2 text-white text-sm font-semibold tracking-wide mb-6">
                                    <TbRocket className="text-[#13B5A6] w-5 h-5" /> {t("footer.getStarted")}
                                </h3>
                                <ul className="flex flex-col gap-4">
                                    {GET_STARTED_LINKS.map(link => (
                                        <li key={link}>
                                            <Link to="/" onClick={() => handleLinkClick("/")} className="group flex items-center justify-between text-[13px] text-white hover:text-white transition-colors w-full sm:w-[85%]">
                                                {t(`footer.links.${link}`)} <TbChevronRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#13B5A6]" />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                        </div>
                    </motion.div>

                    {/* Column 3: Right CTA & Socials */}
                    <motion.div variants={itemVariants} className="lg:col-span-3 lg:pl-12 flex flex-col justify-start relative z-20">
                        <h2 className="text-white text-2xl font-bold mb-4 tracking-wide leading-tight">
                            {t("footer.letsBuild1")}<br />{t("footer.letsBuild2")}
                        </h2>
                        
                        <button   
                        onClick={() => {
                            setActiveLink("/contactus");
                            toggledropdown("contactus");
                        }} className="w-full bg-gradient-to-r gap-2 max-w-fit from-[#0D8780] to-[#0B5C6C] hover:from-[#109C94] hover:to-[#0D6D80] text-white rounded-[4px] px-5 py-3.5 flex justify-between items-center transition-all duration-300 shadow-lg shadow-[#0CA599]/10 group">
                            <span className="flex items-center gap-3 text-[14px] font-semibold tracking-wide">
                                <TbCalendarEvent className="w-5 h-5" /> {t("footer.requestDemoBtn")}
                            </span>
                            <TbArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </button>

                        <div className="mt-6">
                            <h4 className="text-xs font-semibold text-slate-300 tracking-[0.15em] mb-4 uppercase">{t("footer.stayConnected")}</h4>
                            <div className="flex items-center gap-3">
                                {[FaLinkedinIn, FaTwitter, FaYoutube, FaEnvelope].map((SocialIcon, i) => (
                                    <a key={i} href="#" className="w-10 h-10 rounded-full border bg-[#0A121E] flex items-center justify-center text-white border-[#13B5A6] hover:text-white hover:border-2 hover:border-[#10a394] hover:bg-[#0B2530] transition-all">
                                        <SocialIcon className="w-4 h-4" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </motion.div>
            
            {/* --- BOTTOM BAR --- */}
            <motion.div 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="px-10 py-6 flex flex-col border-t-2 border-[#1C2838] md:flex-row items-center justify-between gap-4 md:gap-0 text-[12px] text-white relative z-20"
            >
                <p>© {new Date().getFullYear()} i2Pulse. {t("footer.allRightsReserved")}</p>
                
                <div className="flex flex-wrap items-center justify-center gap-6">
                    <Link to="/privacy-policy" className="text-white hover:text-slate-300 transition-colors duration-200">{t("footer.bottomLinks.privacy")}</Link>
    <Link to="/terms-n-condition" className="text-white hover:text-slate-300 transition-colors duration-200">{t("footer.bottomLinks.terms")}</Link>
    <Link to="/" className="text-white hover:text-slate-300 transition-colors duration-200">{t("footer.bottomLinks.security")}</Link>
    <Link to="/" className="text-white hover:text-slate-300 transition-colors duration-200">{t("footer.bottomLinks.compliance")}</Link>
    
    <button 
        className="text-white hover:text-slate-300 transition-colors duration-200"
        onClick={() => {
            setActiveLink("/contactus");
            toggledropdown("contactus");
        }}
    >
        {t("footer.contactUs")}
    </button>
                    
                    {/* Custom Inline Language Dropdown */}
                    <div className="relative" ref={dropdownRef}>
                        <div 
                            className="flex items-center gap-1.5 ml-2 cursor-pointer text-white transition-colors"
                            onClick={toggleLanguageDropdown}
                        >
                            <TbWorld className="w-4 h-4" />
                            <span>{activeLanguageLabel}</span>
                            <TbChevronRight className={`w-3 h-3 transition-transform duration-300 ${languageDropdownOpen ? "-rotate-90" : "rotate-90"}`} />
                        </div>

                        {/* Dropdown Menu */}
                        {languageDropdownOpen && (
                            <motion.ul
                                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                                transition={{ duration: 0.2 }}
                                className="absolute bottom-full right-0 mb-3 py-2 w-32 bg-[#0A121E] border border-[#1C2838] shadow-lg shadow-black/50 rounded-lg z-50 flex flex-col"
                            >
                                {LANGUAGES.map((lan) => (
                                    <li
                                        key={lan.code}
                                        className={`py-2 px-4 cursor-pointer transition-colors text-center text-[13px] hover:bg-[#13B5A6]/10 text-white ${
                                            lang === lan.code ? "text-[#13B5A6] font-semibold" : "text-slate-400"
                                        }`}
                                        onClick={() => handleLanguageChange(lan.code)}
                                    >
                                        {lan.label}
                                    </li>
                                ))}
                            </motion.ul>
                        )}
                    </div>
                </div>
            </motion.div>
        </footer>
    );
};

export default Footer;